import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import Link from 'next/link';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { liveEventsKey } from 'root/src/shared/constants/keys';
import { DropSerializer } from 'root/src/shared/drops/drops.constants';
import { DropsTabStatus, DROPS_STALE_TIME_MS } from 'root/src/client/v2/_pages/drops/fixtures/drops.constants';
import { DROP_TAB_STATUS } from 'root/src/client/v2/_pages/drops/redesign/dropStatus';
import { buildAssetUrl } from 'root/src/client/v2/common/helpers/assetUrl';
import { upscaleBoxArt, pickHeroImage } from 'root/src/client/v2/_pages/drops/redesign/dropImage';
import defaultPublisherLogo from 'root/src/client/public/default-publisher-logo.png';
import { routeUrls } from 'root/src/shared/descriptions/routes/routeUrls';
import { DarePlatformIcon, PlatformIconColor } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { Img } from 'root/src/client/v2/common/components/ui/image';
const FEATURED_LIMIT = 5;
const SLIDE_INTERVAL_MS = 6000;
const HeroSkeleton = () => (<div className="relative h-[78vh] min-h-[460px] overflow-hidden">
		<Skeleton className="h-full w-full rounded-none"/>
	</div>);
export const DropHeroCarousel = () => {
    var _a;
    const query = trpc.drops.getDrops.useQuery({
        section: liveEventsKey,
        serializer: DropSerializer.LIST_VIEW,
    }, {
        staleTime: DROPS_STALE_TIME_MS,
    });
    const featured = useMemo(() => { var _a, _b; return ((_b = (_a = query.data) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : []).slice(0, FEATURED_LIMIT); }, [query.data]);
    const count = featured.length;
    const [slide, setSlide] = useState(0);
    const [autoAdvance, setAutoAdvance] = useState(true);
    const reduceMotion = useReducedMotion();
    // Mount the visible slide and the one after it, nothing else. Every slide used to render its full
    // 1920px source immediately, so the hero, which is the entire first screen, downloaded five
    // full-width images at once and the largest-contentful-paint image competed with four equally large
    // siblings. Slides stay mounted once shown, so stepping back through them never refetches.
    const [mounted, setMounted] = useState([0]);
    useEffect(() => {
        if (!count) {
            return;
        }
        const upcoming = (slide + 1) % count;
        setMounted(prev => (prev.includes(slide) && prev.includes(upcoming)
            ? prev
            : Array.from(new Set([...prev, slide, upcoming]))));
    }, [slide, count]);
    const next = useCallback(() => setSlide(value => (count ? (value + 1) % count : 0)), [count]);
    const prev = useCallback(() => setSlide(value => (count ? (value - 1 + count) % count : 0)), [count]);
    // Stepping through by hand hands the carousel over for the rest of the visit. The interval used to
    // keep its original schedule no matter what the arrows and dots did, so a click landing late in the
    // 6s window was overtaken almost at once and the slide the visitor picked flashed past.
    const goPrev = useCallback(() => {
        setAutoAdvance(false);
        prev();
    }, [prev]);
    const goNext = useCallback(() => {
        setAutoAdvance(false);
        next();
    }, [next]);
    const goTo = useCallback((index) => {
        setAutoAdvance(false);
        setSlide(index);
    }, []);
    useEffect(() => {
        if (count <= 1 || !autoAdvance) {
            return undefined;
        }
        const timer = setInterval(next, SLIDE_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [next, count, autoAdvance]);
    useEffect(() => {
        if (count > 0 && slide >= count) {
            setSlide(0);
        }
    }, [count, slide]);
    if (query.isLoading) {
        return <HeroSkeleton />;
    }
    if (!count) {
        return null;
    }
    const status = DROP_TAB_STATUS[DropsTabStatus.Live];
    const current = featured[Math.min(slide, count - 1)];
    if (!current) {
        return null;
    }
    const brandImage = (_a = buildAssetUrl(current.brand.img, { w: 160, q: 80, fmt: 'webp' })) !== null && _a !== void 0 ? _a : defaultPublisherLogo.src;
    return (<section className="relative h-[78vh] min-h-[460px] overflow-hidden">
			{featured.map((drop, index) => (mounted.includes(index) ? (<div key={drop.id} className={cn('absolute inset-0 transition-opacity duration-700', index === slide ? 'opacity-100' : 'pointer-events-none opacity-0')}>
					<motion.img src={buildAssetUrl(upscaleBoxArt(pickHeroImage(drop)), { w: 1920, q: 85, fmt: 'webp' })} alt={drop.label} className="h-full w-full object-cover" initial={false} animate={{ scale: !reduceMotion && index === slide ? 1.08 : 1 }} transition={{ duration: index === slide ? 6 : 0.7, ease: 'linear' }}/>
					<div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-background/20"/>
					<div className="absolute inset-0 bg-gradient-to-r from-background/70 via-transparent to-transparent"/>
				</div>) : null))}

			<div className="relative z-10 flex h-full items-end px-4 pb-16 sm:px-6 lg:px-8">
				<div className="mx-auto w-full max-w-7xl">
					<motion.div key={current.id} initial={{ opacity: 0, y: reduceMotion ? 0 : 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }} className="max-w-2xl">
						<span className={cn('inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold', status.badge)}>
							<span className={cn('h-2 w-2 rounded-full', status.dot)}/>
							{status.label}
						</span>

						<h1 className="mt-4 font-display text-4xl font-extrabold text-foreground sm:text-5xl lg:text-6xl">{current.label}</h1>
						<p className="mt-4 line-clamp-3 max-w-lg text-base text-muted-foreground sm:text-lg">{current.description}</p>

						<div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-3">
							<div className="flex items-center gap-2">
								<Img src={brandImage} alt={current.brand.name} className="h-6 w-6 rounded-full border border-solid border-white/10 object-cover"/>
								<span className="text-sm font-medium text-foreground">{current.brand.name}</span>
							</div>
							{current.platforms.length > 0 && (<ul className="flex items-center gap-1.5">
									{current.platforms.map(platform => (<li key={platform} className="flex h-6 w-6 items-center justify-center rounded-full bg-white">
											<DarePlatformIcon size="14" platform={platform} color={PlatformIconColor.Dark}/>
										</li>))}
								</ul>)}
						</div>

						<Link href={routeUrls.drop(current.mnemonicId)} passHref>
							<a className="gleam mt-8 inline-flex items-center gap-2 rounded-lg bg-gradient-brand px-8 py-3 text-sm font-medium text-white no-underline transition-opacity hover:opacity-90">
								View Drop
								<ArrowForwardIcon sx={{ fontSize: 16 }}/>
							</a>
						</Link>
					</motion.div>
				</div>
			</div>

			{count > 1 && (<div className="absolute bottom-8 right-4 z-10 flex items-center gap-3 sm:right-8 lg:right-12">
					<button type="button" onClick={goPrev} aria-label="Previous slide" className="flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60">
						<ChevronLeftIcon sx={{ fontSize: 20 }}/>
					</button>
					<div className="flex items-center gap-2">
						{featured.map((drop, index) => (<button key={drop.id} type="button" onClick={() => goTo(index)} aria-label={`Go to slide ${index + 1}`} className={cn('h-2 rounded-full transition-all', index === slide ? 'w-8 bg-primary' : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50')}/>))}
					</div>
					<button type="button" onClick={goNext} aria-label="Next slide" className="flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60">
						<ChevronRightIcon sx={{ fontSize: 20 }}/>
					</button>
				</div>)}
		</section>);
};
